import * as tslib_1 from "tslib";
import { HttpClient } from '@angular/common/http';
import { of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { UbiAuthFactory, UbiAuthISOCognitoUsernamePasswordService } from 'uber-ui';
import { ApiAbstractService } from './api-abstract.service';
import { environment } from 'environments/environment';
var AuthService = /** @class */ (function (_super) {
    tslib_1.__extends(AuthService, _super);
    function AuthService(ubiAuthFactory, httpClient) {
        var _this = _super.call(this, environment.API_BASE) || this;
        _this.ubiAuthFactory = ubiAuthFactory;
        _this.httpClient = httpClient;
        _this.authorizer = _this.ubiAuthFactory.create(UbiAuthISOCognitoUsernamePasswordService);
        _this.authorizer.configurations({
            userPoolId: environment.POOL_ID,
            clientId: environment.CLIENT_ID,
            endpoint: environment['AUTHORIZER_ENDPOINT'] ? environment['AUTHORIZER_ENDPOINT'] : ''
        });
        return _this;
    }
    /**
     * Do login
     * @method login
     */
    AuthService.prototype.login = function (credentials) {
        return this.authorizer.login(credentials)
            .pipe(map(function (response) {
            if (response.challenge) {
                return {
                    user: response.user,
                    challenge: response.challenge
                };
            }
            return {
                user: {
                    username: response.user['cognito:username'],
                    lastChangePassword: response.user['custom:last_change_password'],
                    lastLogin: response.user['custom:last_login'],
                    email: response.user.email,
                    sub: response.user.sub,
                    name: response.user.name
                },
                token: response.token,
                accessToken: response.accessToken
            };
        }));
    };
    /**
     * Do logout
     * @method logOut
     */
    AuthService.prototype.logOut = function () {
        return this.authorizer.logout()
            .pipe(map(function () { return true; }), catchError(function () { return of(true); }) // do logout even failed
        );
    };
    /**
     * Confirm mail or username when forgot password
     * @method forgotPassword
     */
    AuthService.prototype.forgotPassword = function (email) {
        return this.authorizer.forgotPassword(email);
    };
    /**
     * Reset password
     * @method resetPassword
     */
    AuthService.prototype.resetPassword = function (username, code, newPassword) {
        return this.authorizer.confirmForgotPassword(username, code, newPassword)
            .pipe(map(function () { return true; }));
    };
    /**
     * Change password
     * @method changePassword
     */
    AuthService.prototype.changePassword = function (oldPassword, newPassword) {
        return this.authorizer.changePassword(this.cognitoUser, oldPassword, newPassword)
            .pipe(map(function () { return true; }));
    };
    /**
     * Get current user
     * @method getUser
     */
    AuthService.prototype.getUser = function (bypassCache) {
        var _this = this;
        if (bypassCache === void 0) { bypassCache = false; }
        return this.authorizer.getUser(bypassCache).pipe(map(function (user) {
            _this.cognitoUser = user;
            return {
                username: user.username,
                lastChangePassword: user.attributes['custom:last_change_password'],
                lastLogin: user.attributes['custom:last_login'],
                email: user.attributes.email,
                sub: user.attributes.sub,
                name: user.attributes.name,
                preferredMFA: user.preferredMFA,
                signInUserSession: user.signInUserSession
            };
        }), catchError(function () { return throwError('NotAuthenticated'); }));
    };
    /**
     * Get current session
     * @method getSession
     */
    AuthService.prototype.getSession = function () {
        return this.authorizer.getSession()
            .pipe(map(function (session) { return session; }), catchError(function () { return throwError('NotAuthenticated'); }));
    };
    /**
     * Reset the password for first login
     * @method completeFirstLogin
     */
    AuthService.prototype.completeFirstLogin = function (user, newPassword) {
        return this.authorizer.completeNewPassword(user, newPassword);
    };
    /**
     * Confirm ninety days password
     * @method confirmNinetyDaysChangePassword
     */
    AuthService.prototype.confirmNinetyDaysChangePassword = function (user) {
        return this.authorizer.confirmNinetyDaysChangePassword(user)
            .pipe(map(function () { return true; }));
    };
    /**
     * Update user name
     * @method updateUserName
     */
    AuthService.prototype.updateUserName = function (userName) {
        return this.authorizer.updateAttributes(this.cognitoUser, { name: userName })
            .pipe(map(function (response) {
            return response === 'SUCCESS';
        }));
    };
    /**
     * Update user name to DB
     * @method updateUserNameDB
     */
    AuthService.prototype.updateUserNameDB = function (oauthID, name) {
        return this.httpClient.put(environment.API_BASE + "/api/users/" + oauthID + "/", { id: oauthID, name: name });
    };
    /**
     * Links username together with token in db
     * @method synchronizeUserName
     */
    AuthService.prototype.synchronizeUserName = function (sub, name) {
        return this.httpClient.patch(this.buildUrl("/api/users/" + sub + "/"), { name: name });
    };
    /**
     * Enable MFA
     * @method enableMFA
     */
    AuthService.prototype.enableMFA = function () {
        return this.authorizer.enableMfa(this.cognitoUser);
    };
    /**
     * Confirm to enable MFA
     * @method enableMFAConfirm
     */
    AuthService.prototype.enableMFAConfirm = function (code) {
        return this.authorizer.enableMfaConfirm(this.cognitoUser, code)
            .pipe(map(function () { return true; }));
    };
    /**
     * Disable MFA
     * @method disableMFA
     */
    AuthService.prototype.disableMFA = function () {
        return this.authorizer.disableMfa(this.cognitoUser)
            .pipe(map(function () { return true; }));
    };
    /**
     * Confirm MFA to login
     * @method twoFAConfirm
     */
    AuthService.prototype.twoFAConfirm = function (username, code) {
        return this.authorizer.twoFAConfirm(username, code, 'SOFTWARE_TOKEN_MFA');
    };
    /**
     * Resend code to confirm
     * @method twoFAResend
     */
    AuthService.prototype.twoFAResend = function (identifier) {
        return this.authorizer.twoFAResend(identifier);
    };
    /**
     * Get current access token
     * @method getToken
     */
    AuthService.prototype.getToken = function () {
        return this.authorizer.getToken();
    };
    /**
     * Get current auth state
     * @method getAuthState
     */
    AuthService.prototype.getAuthState = function () {
        return this.authorizer.authState();
    };
    /**
     * Get user roles by current domain
     * @method getRoles
     */
    AuthService.prototype.getRoles = function (userId, domain) {
        var url = environment.API_BASE + "/api/users/" + userId + "/roles/";
        return this.httpClient.get(url).pipe(map(function (res) { return res.filter(function (i) { return i.domain === domain; }); }));
    };
    /**
     * Check DB existence
     * @method checkDbExistence
     */
    AuthService.prototype.checkDbExistence = function (oauthId) {
        return this.httpClient.get(environment.API_BASE + "/api/users/" + oauthId + "/");
    };
    return AuthService;
}(ApiAbstractService));
export { AuthService };
